import type {ReactElement} from 'react';
import {useRef} from 'react';
import {InputField} from '@Components/input-field-v2';
import type {InputFieldControlItemData} from '@Components/input-field-v2/input-field.types';
import {InputControlItemType, InputFieldSize} from '@Components/input-field-v2/input-field.types';
import {Text, TextSize} from '@Components/text';
import {useTopBottomScrollShadow} from '@/hooks/useTopBottomScrollShadow';
import {ReelMusicTrackList} from '../reel-music-track-list/reel-music-track-list';
import {useReelMusicSearch} from './reel-music-search.hooks';
import type {ReelMusicSearchProps} from './reel-music-search.types';
import {REEL_MUSIC_SEARCH_CONTROL_ID, ReelMusicSearchLayout, ReelMusicSearchState} from './reel-music-search.types';
import {assertNever} from '@Utils/assert-never.util';
import styles from './reel-music-search.module.scss';

export function ReelMusicSearch({accountId, chosenReelMusic, onSelectTrack, layout}: ReelMusicSearchProps): ReactElement {
  const reelMusicSearch = useReelMusicSearch(accountId, chosenReelMusic);
  const resultsRef = useRef<HTMLDivElement>(null);
  const {topShadow} = useTopBottomScrollShadow(resultsRef, reelMusicSearch.hasSearchQuery);

  const getControls = (): InputFieldControlItemData[] => {
    const controls: InputFieldControlItemData[] = [];

    if (reelMusicSearch.hasSearchQuery) {
      controls.push({
        id: REEL_MUSIC_SEARCH_CONTROL_ID.CLEAR,
        icon: 'icon-close',
        type: InputControlItemType.ICONS_ONLY,
      });
    }

    controls.push({
      id: REEL_MUSIC_SEARCH_CONTROL_ID.SEARCH,
      icon: 'icon-search',
      isLoading: reelMusicSearch.hasSearchQuery && reelMusicSearch.searchState === ReelMusicSearchState.LOADING,
      type: InputControlItemType.ICONS_ONLY,
    });

    return controls;
  };

  const onControlClick = (id: string): void => {
    if (id === REEL_MUSIC_SEARCH_CONTROL_ID.CLEAR) {
      reelMusicSearch.changeSearchQuery('');
    }
  };

  const getSearchFieldSize = (): InputFieldSize => {
    switch (layout) {
      case ReelMusicSearchLayout.DROPDOWN:
        return InputFieldSize.XSMALL;
      case ReelMusicSearchLayout.BOTTOM_SHEET:
        return InputFieldSize.SMALL;
      default:
        return assertNever(layout);
    }
  };

  const getSearchInputClassName = (): string => {
    switch (layout) {
      case ReelMusicSearchLayout.DROPDOWN:
        return styles.searchInput;
      case ReelMusicSearchLayout.BOTTOM_SHEET:
        return `${styles.searchInput} ${styles.largeTextSearchInput}`;
      default:
        return assertNever(layout);
    }
  };

  const getHeading = (): ReactElement | null => {
    switch (layout) {
      case ReelMusicSearchLayout.DROPDOWN:
        return (
          <div className={`flex-v-row ${styles.heading}`}>
            <Text val={window.i18next.t('pmwjs_reel_music_search_title')} size={TextSize.SM} bold className="ct-neutral-primary" />
            <Text val={window.i18next.t('pmwjs_reel_music_search_subtitle')} size={TextSize.SM} className={styles.subtitle} />
          </div>
        );
      case ReelMusicSearchLayout.BOTTOM_SHEET:
        return null;
      default:
        return assertNever(layout);
    }
  };

  const getResults = (): ReactElement | null => {
    if (!reelMusicSearch.hasSearchQuery) {
      return null;
    }

    return (
      <ReelMusicTrackList
        tracks={reelMusicSearch.tracks}
        searchState={reelMusicSearch.searchState}
        selectedAudioId={chosenReelMusic?.audioId ?? null}
        onSelectTrack={onSelectTrack}
        onRetry={reelMusicSearch.retrySearch}
      />
    );
  };

  return (
    <div className="flex-v-row">
      <div className={styles.search}>
        {getHeading()}
        <InputField
          isControlled
          autoFocus
          size={getSearchFieldSize()}
          inputClassName={getSearchInputClassName()}
          input={reelMusicSearch.searchQuery}
          placeholder={window.i18next.t('pmwjs_reel_music_search_songs')}
          onInputChange={(value): void => {
            reelMusicSearch.changeSearchQuery(String(value));
          }}
          controls={{
            showDefaultControls: false,
            showSingleDefaultControlOnly: false,
            customControls: getControls(),
            onClick: onControlClick,
            className: styles.searchControls,
          }}
        />
      </div>
      <div className={styles.resultsFrame}>
        <div ref={resultsRef} className={`${styles.results} ${topShadow ? styles.topShadow : ''}`}>
          {getResults()}
        </div>
      </div>
    </div>
  );
}
